/**
 * CoPilotLauncher — the floating "Ask Tonnie" button (BL-217).
 *
 * The discoverability fix: the old launcher was a bare `Sparkles` icon lost among
 * five identical header ghost-buttons. This is a persistent, LABELED floating
 * action button (mascot + "Ask Tonnie") pinned bottom-right — the Intercom/
 * assistant-widget pattern — so users always know where the Companion is.
 *
 * Presentational only: the panel's open state + toggle are app-specific
 * (`useCoPilotSidebar` lives per app), so they come in as props. Hidden while the
 * panel is open (the panel owns its own close). On narrow screens it collapses to
 * just the mascot disc.
 *
 * It can be dragged out of the way (owner, 19 Oct 2026): press and move — by
 * mouse, pen or finger — and it follows, kept inside the window; a press that
 * barely moves is still a click and opens the Companion. Where it was left is
 * not kept: a reload puts it back in its corner.
 */

import { HeroAvatar } from '@tonnie/assets';
import { useTranslation } from '@tonnie/locale';
import { cn } from '@tonnie/ui';
import { useEffect, useRef, useState, type PointerEvent } from 'react';

/**
 * Room a page leaves at its bottom so the launcher never covers its last
 * controls (a Delete button, a switch, a card's action) — the launcher sits
 * `bottom-5` and is ~44px tall. Every layout that mounts the launcher pads its
 * content with this.
 */
export const COPILOT_LAUNCHER_CLEARANCE = 'pb-24';

/**
 * Where another floating control sits so it stacks ABOVE the launcher instead
 * of on top of it (same right edge, one launcher higher).
 */
export const COPILOT_LAUNCHER_STACKED = 'bottom-20 right-5';

/** How far a press moves before it is a drag, not a click (px) — a hand's tremor stays a click. */
const DRAG_THRESHOLD_PX = 6;

/** The gap a dragged launcher keeps from the window's edges (px). */
const EDGE_GAP_PX = 8;

interface Point {
  x: number;
  y: number;
}

/** A top-left corner that keeps a box of `size` inside the window. */
function insideWindow(at: Point, size: { width: number; height: number }): Point {
  const clamp = (value: number, max: number) =>
    Math.min(Math.max(value, EDGE_GAP_PX), Math.max(EDGE_GAP_PX, max - EDGE_GAP_PX));
  return {
    x: clamp(at.x, window.innerWidth - size.width),
    y: clamp(at.y, window.innerHeight - size.height),
  };
}

/**
 * Drag a fixed element by pointer, a click still a click: the place it is
 * dragged to (null until it is), and the handlers to spread on it. `onClick`
 * fires only for a press that did not become a drag.
 */
function useDraggable(onClick: () => void) {
  const ref = useRef<HTMLButtonElement>(null);
  const [at, setAt] = useState<Point | null>(null);
  const press = useRef<{ start: Point; origin: Point; moved: boolean } | null>(null);
  const dragged = useRef(false);

  // The window shrinks: a launcher left near an edge comes back inside it.
  useEffect(() => {
    if (at === null) return;
    const keepInside = () => {
      const box = ref.current?.getBoundingClientRect();
      if (box) setAt((current) => (current ? insideWindow(current, box) : current));
    };
    window.addEventListener('resize', keepInside);
    return () => window.removeEventListener('resize', keepInside);
  }, [at]);

  return {
    ref,
    at,
    handlers: {
      onPointerDown: (event: PointerEvent<HTMLButtonElement>) => {
        if (event.button !== 0) return;
        const box = event.currentTarget.getBoundingClientRect();
        press.current = {
          start: { x: event.clientX, y: event.clientY },
          origin: { x: box.left, y: box.top },
          moved: false,
        };
        event.currentTarget.setPointerCapture(event.pointerId);
      },
      onPointerMove: (event: PointerEvent<HTMLButtonElement>) => {
        const current = press.current;
        if (!current) return;
        const dx = event.clientX - current.start.x;
        const dy = event.clientY - current.start.y;
        if (!current.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
        current.moved = true;
        const box = event.currentTarget.getBoundingClientRect();
        setAt(insideWindow({ x: current.origin.x + dx, y: current.origin.y + dy }, box));
      },
      onPointerUp: (event: PointerEvent<HTMLButtonElement>) => {
        dragged.current = press.current?.moved === true;
        press.current = null;
        event.currentTarget.releasePointerCapture(event.pointerId);
      },
      onPointerCancel: () => {
        press.current = null;
      },
      onClick: () => {
        // The click that ends a drag is the drop, not a request to open.
        if (dragged.current) {
          dragged.current = false;
          return;
        }
        onClick();
      },
    },
  };
}

export interface CoPilotLauncherProps {
  /** Whether the Companion panel is currently open (the FAB hides while open). */
  open: boolean;
  /** Open the Companion panel. */
  onOpen: () => void;
  className?: string;
}

export function CoPilotLauncher({ open, onOpen, className }: CoPilotLauncherProps) {
  const { t } = useTranslation();
  // Kept while the panel is open, so the launcher comes back where it was left.
  const { ref, at, handlers } = useDraggable(onOpen);
  if (open) return null;

  return (
    <button
      ref={ref}
      type="button"
      {...handlers}
      data-testid="copilot-launcher"
      aria-label={t('copilot.launcher.label')}
      style={at ? { left: at.x, top: at.y, right: 'auto', bottom: 'auto' } : undefined}
      className={cn(
        'group fixed bottom-5 right-5 z-40 flex touch-none select-none items-center gap-2 rounded-full',
        'border border-copilot-border bg-copilot-surface text-copilot-foreground',
        'py-1.5 pl-1.5 pr-2 shadow-lg shadow-copilot-accent/25 sm:pr-4',
        'transition-transform duration-150 hover:scale-105 active:scale-95',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-copilot-accent focus-visible:ring-offset-2',
        className
      )}
    >
      <span className="flex size-8 items-center justify-center overflow-hidden rounded-full bg-copilot-accent/10">
        <HeroAvatar hero="copilot" size="sm" className="size-7" />
      </span>
      <span className="hidden pr-1 text-sm font-semibold sm:inline">
        {t('copilot.launcher.label')}
      </span>
    </button>
  );
}

export default CoPilotLauncher;
